import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from '@/lib/i18n';
import { Button, Dialog, DialogContent, DialogDescription, DialogTitle, Input, Label, RequiredMark } from '@/components/ui';
import {
  emptySettlementGenerateValues,
  parseVendorIds,
  settlementGenerateSchema,
  type SettlementGenerateValues,
} from '../schemas/settlementFormSchema';

export interface GenerateCycleModalProps {
  isOpen: boolean;
  isSubmitting: boolean;
  errorMessage?: string | null;
  onSubmit: (input: { cycleId: string; body: { vendorIds?: string[]; note?: string | null; runType?: 'MANUAL' } }) => void;
  onCancel: () => void;
}

export const GenerateCycleModal = ({
  isOpen,
  isSubmitting,
  errorMessage,
  onSubmit,
  onCancel,
}: GenerateCycleModalProps) => {
  const { t } = useTranslation('settlement');
  const form = useForm<SettlementGenerateValues>({
    resolver: zodResolver(settlementGenerateSchema),
    defaultValues: emptySettlementGenerateValues(),
    mode: 'onBlur',
  });

  const fieldError = (name: keyof SettlementGenerateValues): string | undefined => {
    const e = form.formState.errors[name];
    if (e && typeof e === 'object' && 'message' in e && typeof e.message === 'string') {
      return e.message;
    }
    return undefined;
  };

  const submit = (values: SettlementGenerateValues) => {
    const vendorIds = values.vendorIds ? parseVendorIds(values.vendorIds) : [];
    const body: { vendorIds?: string[]; note?: string | null; runType?: 'MANUAL' } = {
      runType: 'MANUAL',
    };
    if (vendorIds.length > 0) body.vendorIds = vendorIds;
    if (values.note && values.note.length > 0) body.note = values.note;
    onSubmit({ cycleId: values.cycleId, body });
  };

  return (
    <Dialog open={isOpen} onOpenChange={(o) => { if (!o && !isSubmitting) onCancel(); }}>
      <DialogContent data-testid="generate-cycle-modal" className="max-w-md">
        <DialogTitle>{t('settlement.generate.title')}</DialogTitle>
        <DialogDescription>{t('settlement.generate.subject')}</DialogDescription>

        <form
          onSubmit={form.handleSubmit(submit)}
          className="flex flex-col gap-3"
          noValidate
        >
          <div className="flex flex-col gap-1">
            <Label htmlFor="cycle-id">
              {t('settlement.generate.cycleIdLabel')}
              <RequiredMark />
            </Label>
            <Input
              id="cycle-id"
              {...form.register('cycleId')}
              placeholder="MONTHLY-2026-05"
              maxLength={32}
              aria-invalid={Boolean(fieldError('cycleId'))}
              style={{ height: 'var(--density-compact-form-h)' }}
            />
            <span className="text-[10px] text-[color:var(--content-muted)]">
              {t('settlement.generate.cycleIdHint')}
            </span>
            {fieldError('cycleId') ? (
              <span
                role="alert"
                className="text-xs"
                style={{ color: 'var(--signal-danger)' }}
              >
                {t(fieldError('cycleId')!, { defaultValue: fieldError('cycleId')! })}
              </span>
            ) : null}
          </div>

          <div className="flex flex-col gap-1">
            <Label htmlFor="vendor-ids">{t('settlement.generate.vendorIdsLabel')}</Label>
            <Input
              id="vendor-ids"
              {...form.register('vendorIds')}
              placeholder="VND_A, VND_B, ..."
              style={{ height: 'var(--density-compact-form-h)' }}
            />
            <span className="text-[10px] text-[color:var(--content-muted)]">
              {t('settlement.generate.vendorIdsHint')}
            </span>
          </div>

          <div className="flex flex-col gap-1">
            <Label htmlFor="generate-note">{t('settlement.generate.noteLabel')}</Label>
            <textarea
              id="generate-note"
              {...form.register('note')}
              maxLength={500}
              rows={3}
              className="w-full rounded-md border px-3 py-2 text-sm bg-[color:var(--surface-0)] text-[color:var(--content-strong)] focus-visible:outline-none focus-visible:ring-2"
              style={{ borderColor: 'var(--content-muted)' }}
            />
          </div>

          {errorMessage ? (
            <p
              role="alert"
              className="rounded-md p-2 text-sm"
              style={{
                background: 'var(--status-danger-bg)',
                color: 'var(--status-danger-fg)',
              }}
            >
              {errorMessage}
            </p>
          ) : null}

          <div className="mt-2 flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              size="md"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              {t('settlement.actions.cancel')}
            </Button>
            <Button type="submit" variant="cta" size="md" disabled={isSubmitting}>
              {isSubmitting
                ? t('settlement.generate.submitting')
                : t('settlement.generate.submit')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
